import React, { ReactNode } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box } from '@mui/material';
import { SourceIconsFormatterClassNames } from '../../../views/CustomDashboards/components/LabelFormatters/SourceIconsFormatter.utils';
import { TickRotation } from './AvComposedWidget.utils';

const AXIS = {
  X: 'X',
  Y: 'Y',
} as const;

interface Props {
  rotation?: (typeof TickRotation)[keyof typeof TickRotation];
  axis: (typeof AXIS)[keyof typeof AXIS];
  // Provided by recharts
  // eslint-disable-next-line react/no-unused-prop-types
  height?: number;
  width?: number;
  payload?: { value: string; [key: string]: unknown };
  // Passes by recharts
  // eslint-disable-next-line react/no-unused-prop-types
  className?: string;
  // Passes by recharts
  // eslint-disable-next-line react/no-unused-prop-types
  index?: number;
  x?: number;
  y?: number;
  tickFormatter?: (value: string) => string;
  visibleTicksCount?: number;
  customTickFormatter?: (value: string) => string | ReactNode;
}

export const customTickClassnames = {
  xAxisVertical: `.xAxis .${TickRotation.vertical}`,
  xAxisHorizontal: `.xAxis .${TickRotation.horizontal}`,
  xAxisSlanted: `.xAxis .${TickRotation.slanted}`,
  yAxisSlanted: `.yAxis .${TickRotation.slanted}`,
  yAxisHorizontal: `.yAxis .${TickRotation.horizontal}`,
};

function AvComposedWidgetCustomTick({
  x = 0,
  y = 0,
  payload,
  rotation = TickRotation.horizontal,
  width: barWidth,
  tickFormatter,
  visibleTicksCount,
  customTickFormatter,
  axis,
}: Props) {
  if (!payload || payload.value === undefined) {
    return null;
  }

  const width = (() => {
    if (axis === 'X' && rotation === TickRotation.slanted) {
      return 98;
    }

    if (axis === 'X' && rotation === TickRotation.vertical) {
      return 65;
    }

    if (axis === 'Y' && rotation === TickRotation.horizontal) {
      return 80;
    }

    if (axis === 'Y' && rotation === TickRotation.slanted) {
      return 105;
    }

    if (barWidth && visibleTicksCount) {
      return barWidth / visibleTicksCount;
    }

    return rotation === TickRotation.horizontal ? 100 : axis === AXIS.X ? 85 : 150;
  })();

  return (
    <foreignObject x={x} y={y} width={1} height={1} overflow="visible">
      <Box
        className={rotation}
        sx={{
          position: 'absolute',
          top: axis === 'Y' ? 0 : rotation === TickRotation.horizontal ? '50%' : '8px',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width,
          ...flex.center,
          pointerEvents: 'none',
          [`.yAxis &`]: {
            transform: 'translate(-100%, -50%)',
            justifyContent: 'flex-end',
            height: 'auto',
          },

          [`.yAxis:has(.${SourceIconsFormatterClassNames.multiIcons}) &`]: {
            width: '110px',
          },
        }}>
        {customTickFormatter ? (
          customTickFormatter(payload.value)
        ) : (
          <AvTooltip
            sx={{
              color: theme => theme.palette.colors.neutrals[500],
              fontSize: '12px',
              pointerEvents: 'all',
              [`${customTickClassnames.xAxisVertical} &`]: {
                transform: 'rotate(-90deg) translate(-35%, -25%)',
                transformOrigin: 'center center',
              },
              [`${customTickClassnames.xAxisSlanted} &`]: {
                transform: 'rotate(-45deg) translate(-50%, -50%)',
                transformOrigin: 'center center',
              },

              [`${customTickClassnames.yAxisSlanted} &`]: {
                transform: 'rotate(-45deg)',
                transformOrigin: 'right center',
              },
            }}>
            {tickFormatter?.(payload?.value)}
          </AvTooltip>
        )}
      </Box>
    </foreignObject>
  );
}

export default AvComposedWidgetCustomTick;
